import { Navigate, useParams } from 'react-router-dom';
import { Center } from '@/components/center';
import { Loading } from '@/components/loading';
import { useLogoutMutation, useSessionQuery } from '@/api/login';
import { useShareAcceptDetailsQuery } from '@/api/share';
import { loginFor } from '@/services/emailed-link';
import { ShareMessage } from './share-message';

// Home for a signed-in visitor: the root route routes a session through
// terms, orphan and launch.
const onHome = () => {
  window.location.assign('/');
};

// The emailed share link lands here. The link's job is to get the
// recipient to log in or create an account; accepting happens in the
// pending list.
//
// Without a session the visitor goes to the log-in page for the link,
// where create-an-account is beside it. With a session, one authenticated
// read on the token answers yours-or-not on the live rule: yours, and the
// share is waiting in the pending list; not yours, and they are told so.
// Nothing is accepted or declined here.
const ShareAccept = () => {
  const { token = '' } = useParams<{ token: string }>();
  const { data: session, isLoading: isSessionLoading } = useSessionQuery();
  const loggedIn = !!session?.accessToken;
  const {
    data: details,
    error: detailsError,
    isLoading: isDetailsLoading,
  } = useShareAcceptDetailsQuery(token, { skip: !loggedIn });
  const [logout, { isLoading: isLoggingOut }] = useLogoutMutation();

  if (isSessionLoading || (loggedIn && isDetailsLoading)) {
    return <Loading />;
  }

  if (!loggedIn) {
    return <Navigate to={loginFor({ kind: 'share', token })} replace />;
  }

  if (details?.state === 'pending') {
    return <Navigate to='/invitations' replace />;
  }

  const status = (detailsError as any)?.status;
  const code = (detailsError as any)?.data?.code;
  const notFound = status === 404;
  const notForThisAccount = code === 'share-not-for-this-account';

  return (
    <Center>
      {details?.state === 'spent' && (
        <ShareMessage
          title='Already accepted'
          actions={[{ label: 'Go to Kinesis', onClick: onHome }]}
        >
          You’ve already accepted this share.
        </ShareMessage>
      )}
      {details?.state === 'expired' && (
        <ShareMessage title='Share expired'>
          This share has expired. Ask whoever shared it with you to share it
          again.
        </ShareMessage>
      )}
      {notForThisAccount && (
        <ShareMessage
          title='Not for this account'
          actions={[
            {
              label: 'Log in with another account',
              onClick: () => logout({ to: loginFor({ kind: 'share', token }) }),
              loading: isLoggingOut,
            },
            { label: 'Go to Kinesis', onClick: onHome, variant: 'secondary' },
          ]}
        >
          This share was sent to a different email address. You’re signed in as{' '}
          <strong>{session?.email}</strong>. Ask whoever shared it to send it to
          this address, or log in with the address it was sent to.
        </ShareMessage>
      )}
      {notFound && (
        <ShareMessage title='Share not found'>
          This share isn’t available. Ask whoever shared it with you to share it
          again.
        </ShareMessage>
      )}
      {!details && detailsError && !notFound && !notForThisAccount && (
        <ShareMessage title='Something went wrong'>
          We couldn’t open this share. Please try again.
        </ShareMessage>
      )}
    </Center>
  );
};

export { ShareAccept };
